import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CheckCircle2, ExternalLink, RefreshCcw, RotateCcw, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CopyButton } from '@/components/ui/CopyButton';
import { Textarea } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs } from '@/components/ui/tabs';
import { PostPanel } from '@/components/content/PostPanel';
import { Stat } from '@/components/content/Stat';
import { CONTENT_STATUS_TONE } from '@/constants/content';
import { api } from '@/lib/api';
import { formatCost, formatRelative, shortId } from '@/lib/format';
import type { Account } from '@/types/account';
import type { ContentPostStatus, ContentStatus } from '@/types/content';

type TabKey = 'script' | 'video' | 'assembly' | 'reviews';

const POST_STATUS_TONE: Record<
  ContentPostStatus,
  'neutral' | 'running' | 'success' | 'failure'
> = {
  pending: 'neutral',
  posting: 'running',
  published: 'success',
  failed: 'failure',
};

const POSTS_POLL_MS = 5_000;
const POST_TERMINAL: ReadonlySet<ContentPostStatus> = new Set(['published', 'failed']);

function PostsSection({
  personaId,
  contentId,
}: {
  personaId: string;
  contentId: string;
}) {
  const postsQ = useQuery({
    queryKey: ['content', contentId, 'posts'],
    queryFn: () => api.getContentPosts(contentId),
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (!rows || rows.length === 0) return false;
      // Keep polling while at least one row hasn't reached a terminal state.
      return rows.some((r) => !POST_TERMINAL.has(r.status)) ? POSTS_POLL_MS : false;
    },
  });
  const accountsQ = useQuery({
    queryKey: ['persona', personaId, 'accounts'],
    queryFn: () => api.listPersonaAccounts(personaId),
  });
  const handles = new Map<string, string>(
    (accountsQ.data ?? []).map((a: Account) => [a.account_id, a.handle]),
  );
  const posts = postsQ.data ?? [];

  if (postsQ.isLoading || posts.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Posts</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {posts.map((p) => {
            const handle = handles.get(p.account_id);
            return (
              <li
                key={p.post_id}
                className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
              >
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Badge tone={POST_STATUS_TONE[p.status]}>{p.status}</Badge>
                    <span className="font-mono text-xs text-foreground/90">
                      {handle ? `@${handle}` : shortId(p.account_id)}
                    </span>
                    {p.workflow_id && (
                      <Link
                        to="/runs/$workflowId"
                        params={{ workflowId: p.workflow_id }}
                        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                      >
                        run
                      </Link>
                    )}
                  </div>
                  {p.failure_reason && (
                    <p className="text-xs text-destructive-foreground/90">{p.failure_reason}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {p.posted_at && <span>{formatRelative(p.posted_at)}</span>}
                  {p.post_url && (
                    <a
                      href={p.post_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      view
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

export function ContentDetailPage({ contentId }: { contentId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('video');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  const contentQ = useQuery({
    queryKey: ['content', contentId],
    queryFn: () => api.getContent(contentId),
    refetchOnWindowFocus: true,
  });

  const approveM = useMutation({
    mutationFn: (note?: string) => api.approveContent(contentId, note ? { note } : {}),
    onSuccess: () => {
      toast.success('Content approved.');
      void queryClient.invalidateQueries({ queryKey: ['content', contentId] });
    },
    onError: (err) => toast.error('Approve failed', { description: String(err) }),
  });

  const rejectM = useMutation({
    mutationFn: (note?: string) => api.rejectContent(contentId, note ? { note } : {}),
    onSuccess: () => {
      setRejectOpen(false);
      setRejectNote('');
      toast.success('Content rejected.');
      void queryClient.invalidateQueries({ queryKey: ['content', contentId] });
    },
    onError: (err) => toast.error('Reject failed', { description: String(err) }),
  });

  const regenerateM = useMutation({
    mutationFn: () => api.regenerateContent(contentId, {}),
    onSuccess: (res) => {
      toast.success('Regeneration started; watching the new run.');
      void navigate({ to: '/runs/$workflowId', params: { workflowId: res.workflow_id } });
    },
    onError: (err) => toast.error('Regenerate failed', { description: String(err) }),
  });

  const deleteM = useMutation({
    // Soft-delete → moves content to Trash (recoverable from the Content list).
    mutationFn: () => api.deleteContent(contentId),
    onSuccess: () => {
      toast.success('Content moved to Trash', {
        description: 'Restore it from the Trash view on the Content list.',
      });
      void queryClient.invalidateQueries({ queryKey: ['content'] });
      void navigate({ to: '/content' });
    },
    onError: (err) => toast.error('Delete failed', { description: String(err) }),
  });

  const restoreM = useMutation({
    mutationFn: () => api.restoreContent(contentId),
    onSuccess: () => {
      toast.success('Content restored', { description: 'Moved back out of Trash.' });
      setRestoreOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['content'] });
    },
    onError: (err) => toast.error('Restore failed', { description: String(err) }),
  });

  const purgeM = useMutation({
    mutationFn: () => api.deleteContent(contentId, { purge: true }),
    onSuccess: () => {
      toast.success('Content permanently deleted');
      void queryClient.invalidateQueries({ queryKey: ['content'] });
      void navigate({ to: '/content' });
    },
    onError: (err) => toast.error('Delete failed', { description: String(err) }),
  });

  if (contentQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (contentQ.isError || !contentQ.data) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm">
        Content not found.
      </div>
    );
  }

  const c = contentQ.data;
  const v = c.video;
  const status: ContentStatus = c.status ?? 'draft';
  const reviews = c.reviews ?? [];
  const critique = c.critique ?? null;
  const beats = c.script?.beats ?? [];
  const isTrashed = !!c.deleted_at;
  const canAct =
    !approveM.isPending && !rejectM.isPending && !regenerateM.isPending && !deleteM.isPending;


  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Content</h1>
          <p className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
            {c.content_id}
            <CopyButton value={c.content_id} label={`Copy ${c.content_id}`} />
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={CONTENT_STATUS_TONE[status]}>{status}</Badge>
            {c.pillar && (
              <Badge tone="neutral" title="Content pillar">
                {c.pillar}
              </Badge>
            )}
            {critique && !critique.skipped && (
              <Badge
                tone={critique.final_pass ? 'success' : 'warning'}
                title="Script critique gate result"
              >
                {critique.final_pass ? 'craft check passed' : 'craft check failed'}
              </Badge>
            )}
            <Badge>{v?.provider_model}</Badge>
            <Badge tone="neutral">{v?.mode}</Badge>
            <span className="text-xs text-muted-foreground">
              created {formatRelative(c.created_at)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Link
                to="/personas/$personaId"
                params={{ personaId: c.persona_id }}
                className="text-xs text-muted-foreground hover:underline"
              >
                persona {shortId(c.persona_id)}
              </Link>
              <CopyButton value={c.persona_id} label={`Copy ${c.persona_id}`} />
            </span>
            {c.post_url && (
              <a
                href={c.post_url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-xs text-foreground/80 hover:text-foreground hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                view on Instagram
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isTrashed ? (
            <>
              <Button
                variant="outline"
                onClick={() => setRestoreOpen(true)}
                disabled={restoreM.isPending || purgeM.isPending}
                title="Restore content out of Trash"
              >
                <RotateCcw className="h-4 w-4" />
                {restoreM.isPending ? 'Restoring…' : 'Restore'}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setPurgeOpen(true)}
                disabled={restoreM.isPending || purgeM.isPending}
                title="Permanently delete content + video files"
              >
                <Trash2 className="h-4 w-4" />
                {purgeM.isPending ? 'Deleting…' : 'Delete forever'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => regenerateM.mutate()}
                disabled={!canAct}
                title="Re-trigger ContentGenerationWorkflow with the same persona + brief"
              >
                <RefreshCcw className="h-4 w-4" />
                {regenerateM.isPending ? 'Starting…' : 'Regenerate'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeleteOpen(true)}
                disabled={!canAct}
                title="Move content to Trash"
              >
                <Trash2 className="h-4 w-4" />
                {deleteM.isPending ? 'Deleting…' : 'Delete'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setRejectOpen(true)}
                disabled={!canAct || status === 'rejected' || status === 'published'}
              >
                <XCircle className="h-4 w-4" />
                {rejectM.isPending ? 'Rejecting…' : 'Reject'}
              </Button>
              <Button
                onClick={() => approveM.mutate(undefined)}
                disabled={!canAct || status === 'approved' || status === 'published'}
              >
                <CheckCircle2 className="h-4 w-4" />
                {approveM.isPending ? 'Approving…' : 'Approve'}
              </Button>
            </>
          )}
        </div>
      </header>

      <Tabs<TabKey>
        tabs={[
          { value: 'video', label: 'Video' },
          { value: 'script', label: 'Script' },
          { value: 'assembly', label: 'Assembly' },
          { value: 'reviews', label: 'Reviews', count: reviews.length },
        ]}
        value={tab}
        onChange={setTab}
      />

      {!isTrashed && (
        <PostPanel personaId={c.persona_id} contentId={c.content_id} contentStatus={status} />
      )}

      <PostsSection personaId={c.persona_id} contentId={c.content_id} />

      {tab === 'video' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-2">
                <video
                  controls
                  src={api.videoUrl(c.content_id)}
                  className="aspect-[9/16] w-full max-w-md mx-auto rounded-md bg-black"
                />
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Provider</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Stat label="Model" value={v?.provider_model} />
              <Stat label="Mode" value={v?.mode} />
              <Stat label="Endpoint" value={v?.endpoint} />
              <Stat label="Duration" value={v?.duration_sec ? `${v.duration_sec}s` : '—'} />
              <Stat label="Resolution" value={v?.resolution} />
              <Stat label="Cost" value={formatCost(v?.cost_usd_est)} />
              <Stat label="Seed" value={v?.seed?.toString() ?? '—'} />
              <Stat label="fal request" value={shortId(v?.fal_request_id ?? '')} />
              {v?.cdn_url && (
                <a
                  href={v.cdn_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="block truncate text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  {v.cdn_url}
                </a>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'script' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {(c.script?.hook || c.script?.next_action) && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Hook</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-medium leading-relaxed">{c.script?.hook || '—'}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    First words spoken and first line of the caption.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Next action</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-medium leading-relaxed">
                    {c.script?.next_action || '—'}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Last words spoken and last line of the caption.
                  </p>
                </CardContent>
              </Card>
            </>
          )}
          {critique && !critique.skipped && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Craft check
                  <Badge tone={critique.final_pass ? 'success' : 'warning'}>
                    {critique.final_pass ? 'passed' : 'failed'}
                  </Badge>
                  <span className="text-xs font-normal text-muted-foreground">
                    {critique.attempts.length} attempt{critique.attempts.length === 1 ? '' : 's'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {critique.attempts.map((a, i) => (
                  <div
                    key={i}
                    className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="mr-1 text-xs text-muted-foreground">
                        draft {i + 1}
                      </span>
                      {(
                        ['hook', 'next_action', 'pillar_fit', 'voice_fit', 'platform_norms'] as const
                      ).map((k) => {
                        const failing = a.failing?.includes(k);
                        return (
                          <span
                            key={k}
                            className={
                              'rounded border px-1.5 py-0.5 font-mono text-[11px] ' +
                              (failing
                                ? 'border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-200'
                                : 'border-border text-foreground/80')
                            }
                          >
                            {k.replace('_', ' ')} {a.scores?.[k] ?? '?'}/5
                          </span>
                        );
                      })}
                    </div>
                    {a.notes && <p className="mt-1.5 text-xs text-foreground/90">{a.notes}</p>}
                    {a.fixes && a.fixes.length > 0 && (
                      <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
                        {a.fixes.map((f, j) => (
                          <li key={j}>{f}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Caption</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{c.script?.caption}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{beats.length > 0 ? 'Beats' : 'Visual brief'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {beats.length > 0 ? (
                beats.map((b, i) => (
                  <div key={i}>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Beat {String.fromCharCode(65 + i)} · {i * 5}–{(i + 1) * 5}s
                    </p>
                    {b.voiceover && (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed italic">
                        “{b.voiceover}”
                      </p>
                    )}
                    {b.visual_brief && (
                      <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                        {b.visual_brief}
                      </p>
                    )}
                  </div>
                ))
              ) : c.script?.visual_brief_a || c.script?.visual_brief_b ? (
                <>
                  {c.script?.visual_brief_a && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Beat A · first 10s
                      </p>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">
                        {c.script.visual_brief_a}
                      </p>
                    </div>
                  )}
                  {c.script?.visual_brief_b && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Beat B · second 10s
                      </p>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">
                        {c.script.visual_brief_b}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {c.script?.visual_brief}
                </p>
              )}
              {c.script?.voiceover && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Voiceover
                  </p>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed italic">
                    {c.script.voiceover}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Hashtags</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="flex flex-wrap gap-1.5">
                {(c.script?.hashtags ?? []).map((h) => (
                  <li
                    key={h}
                    className="rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-foreground/90"
                  >
                    #{h.replace(/^#/, '')}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          {c.brief && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Operator brief</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{c.brief}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {tab === 'assembly' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {c.assembly ? (
              <pre className="overflow-x-auto rounded-md bg-muted p-3 text-left text-xs">
                {JSON.stringify(c.assembly, null, 2)}
              </pre>
            ) : (
              <>
                No assembly run for this piece yet. The video-edit (assembler) workflow lands as
                part of Plan 04 — once it runs, the second-pass artifact + recipe show up here.
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'reviews' && (
        <Card>
          <CardContent className="py-4">
            {reviews.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No reviews yet. Approve or reject above to record one.
              </p>
            ) : (
              <ul className="space-y-2">
                {reviews
                  .slice()
                  .reverse()
                  .map((r, i) => (
                    <li
                      key={i}
                      className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
                    >
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <Badge tone={CONTENT_STATUS_TONE[r.decision]}>{r.decision}</Badge>
                          <span className="text-xs text-muted-foreground">by {r.actor}</span>
                        </div>
                        {r.note && <p className="text-xs text-foreground/90">{r.note}</p>}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatRelative(r.timestamp)}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {deleteOpen && (
        <ConfirmDialog
          title="Move to Trash?"
          description="You can restore it later from the Trash view on the Content list."
          confirmLabel="Move to Trash"
          destructive
          pending={deleteM.isPending}
          onConfirm={() => deleteM.mutate()}
          onCancel={() => setDeleteOpen(false)}
        />
      )}

      {rejectOpen && (
        <ConfirmDialog
          title="Reject this content?"
          description={
            <div className="space-y-2">
              <p>It won't be posted. You can regenerate it afterwards.</p>
              <Textarea
                autoFocus
                rows={3}
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Reason (optional) — e.g. mouth out of sync in clip 3"
              />
            </div>
          }
          confirmLabel="Reject"
          destructive
          pending={rejectM.isPending}
          onConfirm={() => rejectM.mutate(rejectNote.trim() || undefined)}
          onCancel={() => setRejectOpen(false)}
        />
      )}

      {restoreOpen && (
        <ConfirmDialog
          title="Restore this content?"
          description="It moves back out of Trash and reappears in the active Content list."
          confirmLabel="Restore"
          pending={restoreM.isPending}
          onConfirm={() => restoreM.mutate()}
          onCancel={() => setRestoreOpen(false)}
        />
      )}

      {purgeOpen && (
        <ConfirmDialog
          title="Delete forever?"
          description="Permanently delete this content and its video files. This cannot be undone."
          confirmLabel="Delete forever"
          destructive
          pending={purgeM.isPending}
          onConfirm={() => purgeM.mutate()}
          onCancel={() => setPurgeOpen(false)}
        />
      )}
    </div>
  );
}
